import { useQuery } from "@msuite/ui/query";
import { DataTable } from "@msuite/ui/table";
import { responseHandler } from "@msuite/ui/utils";
import { guardArray } from "@msuite/utils/array";
import { useMemo } from "react";
import { client } from "@/hono";
import type { ProjectPanelBaseProps } from "../types";
import { ProjectContactsContext } from "./context";
import { ProjectContactHeader } from "./project-contact-header";
import { ProjectContactsMenu } from "./project-contacts-menu";
import { columns } from "./table-config";

export function ProjectContacts({ projectId }: ProjectPanelBaseProps) {
	/** Hooks */

	/** Query */
	const {
		data: contacts,
		refetch,
		isLoading,
		queryKey,
	} = useQuery({
		queryKey: ["project-contacts", projectId],
		queryFn: async () => {
			const response = await client.projects[":projectId"].contacts.$get({
				param: { projectId },
			});
			const data = await responseHandler(response);
			return data.contacts;
		},
	});

	const grouped = useMemo(() => {
		const groupedContacts = contacts?.flatMap((contact) => {
			const members = guardArray(contact._contactMembers).map((member) => {
				member.contactId = contact.id;
				return member;
			});
			return members;
		});
		return groupedContacts;
	}, [contacts]);

	/** Render */
	return (
		<ProjectContactsContext.Provider value={{ projectId }}>
			<DataTable
				data={grouped}
				columns={columns}
				isLoading={isLoading}
				queryKey={queryKey}
				refetch={refetch}
				persistenceKey={`${projectId}-contacts-table`}
				grouping={["contactId"]}
				initialColumnVisibility={{
					contactId: false,
					contactName: false,
				}}
				renderGroupHeader={(row) => (
					<ProjectContactHeader
						contactId={row.original.contactId}
						projectId={projectId}
						refetch={refetch}
						numberOfItems={row.subRows.length}
					/>
				)}
				menu={
					<ProjectContactsMenu
						queryKey={queryKey}
						projectId={projectId}
					/>
				}
			/>
		</ProjectContactsContext.Provider>
	);
}
